import { supabase } from './supabase';
import type { ApiResponse, BankTransaction } from '../types/database';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BankCategoryRule {
  id: string;
  company_id: string;
  exact_description: string;
  category_id: string;
  created_at: string;
  /** Joined field: the human-readable category name */
  accounting_categories?: { name: string } | null;
}

export interface CreateBankCategoryRuleData {
  company_id: string;
  exact_description: string;
  category_id: string;
}

// ─── Postgres error code for unique-constraint violations ─────────────────────
const PG_UNIQUE_VIOLATION = '23505';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true when the Supabase error object represents a UNIQUE constraint
 * violation on the `bank_category_rules(company_id, exact_description)` index.
 */
export function isUniqueViolation(err: unknown): boolean {
  if (err && typeof err === 'object' && 'code' in err) {
    return (err as { code: string }).code === PG_UNIQUE_VIOLATION;
  }
  return false;
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

/**
 * Fetch all bank-category auto-detection rules for the given company.
 * Joins `accounting_categories` so callers get the category name too.
 */
async function getRulesForCompany(
  companyId: string,
): Promise<ApiResponse<BankCategoryRule[]>> {
  try {
    const { data, error } = await supabase
      .from('bank_category_rules')
      .select('*, accounting_categories(name)')
      .eq('company_id', companyId)
      .order('exact_description', { ascending: true });

    if (error) {
      console.error('bankCategoryRulesService.getRulesForCompany error:', error);
      return { data: null, error: { message: error.message, code: error.code, details: error }, status: 400 };
    }

    return { data: data ?? [], error: null, status: 200 };
  } catch (err) {
    console.error('Unexpected error in getRulesForCompany:', err);
    return { data: null, error: { message: 'An unexpected error occurred', details: err }, status: 500 };
  }
}

/**
 * Fetch rules scoped to a specific category (used when opening the modal for
 * one category row in the settings page).
 */
async function getRulesForCategory(
  companyId: string,
  categoryId: string,
): Promise<ApiResponse<BankCategoryRule[]>> {
  try {
    const { data, error } = await supabase
      .from('bank_category_rules')
      .select('*, accounting_categories(name)')
      .eq('company_id', companyId)
      .eq('category_id', categoryId)
      .order('exact_description', { ascending: true });

    if (error) {
      console.error('bankCategoryRulesService.getRulesForCategory error:', error);
      return { data: null, error: { message: error.message, code: error.code, details: error }, status: 400 };
    }

    return { data: data ?? [], error: null, status: 200 };
  } catch (err) {
    console.error('Unexpected error in getRulesForCategory:', err);
    return { data: null, error: { message: 'An unexpected error occurred', details: err }, status: 500 };
  }
}

/**
 * Create a single rule.
 * Returns a typed error payload so callers can distinguish unique-constraint
 * violations from other errors.
 */
async function createRule(
  rule: CreateBankCategoryRuleData,
): Promise<ApiResponse<BankCategoryRule>> {
  try {
    const { data, error } = await supabase
      .from('bank_category_rules')
      .insert([rule])
      .select('*, accounting_categories(name)')
      .single();

    if (error) {
      console.error('bankCategoryRulesService.createRule error:', error);
      return {
        data: null,
        error: { message: error.message, code: error.code, details: error },
        status: error.code === PG_UNIQUE_VIOLATION ? 409 : 400,
      };
    }

    return { data, error: null, status: 201 };
  } catch (err) {
    console.error('Unexpected error in createRule:', err);
    return { data: null, error: { message: 'An unexpected error occurred', details: err }, status: 500 };
  }
}

/**
 * Delete a rule by its primary-key UUID.
 */
async function deleteRule(ruleId: string): Promise<ApiResponse<null>> {
  try {
    const { error } = await supabase
      .from('bank_category_rules')
      .delete()
      .eq('id', ruleId);

    if (error) {
      console.error('bankCategoryRulesService.deleteRule error:', error);
      return { data: null, error: { message: error.message, code: error.code, details: error }, status: 400 };
    }

    return { data: null, error: null, status: 200 };
  } catch (err) {
    console.error('Unexpected error in deleteRule:', err);
    return { data: null, error: { message: 'An unexpected error occurred', details: err }, status: 500 };
  }
}

/**
 * Bulk-fetch all rules for a company and return them as an array.
 * The caller iterates the array and checks whether the transaction description
 * **contains** the rule text (case-insensitive), matching the same semantics
 * used by `findUnclassifiedMatching`.
 */
async function buildRulesList(
  companyId: string,
): Promise<Array<{ text: string; categoryId: string }>> {
  const { data } = await getRulesForCompany(companyId);
  if (!data) return [];
  return data.map((r) => ({
    text: r.exact_description.trim().toLowerCase(),
    categoryId: r.category_id,
  }));
}

/**
 * Given a transaction description and a pre-fetched rules list, return the
 * matching category id or null.  Longer rules are checked first so a more
 * specific rule wins over a shorter/general one.
 */
function matchCategory(
  description: string,
  rules: Array<{ text: string; categoryId: string }>,
): string | null {
  const lower = description.trim().toLowerCase();
  const sorted = [...rules].sort((a, b) => b.text.length - a.text.length);
  for (const rule of sorted) {
    if (lower.includes(rule.text)) return rule.categoryId;
  }
  return null;
}

// ─── Back-fill helpers ────────────────────────────────────────────────────────

/**
 * Find existing bank transactions in `companyId` that:
 *   1. Have a description that CONTAINS `ruleText` (case-insensitive ILIKE).
 *   2. Are NOT already classified under `categoryId` — i.e. their current
 *      `accounting_category_id` is either NULL or a *different* category.
 *      This lets users re-classify transactions that were previously assigned
 *      to the wrong bucket, not only fully unclassified ones.
 *
 * The joined `accounting_categories(name)` is intentionally fetched so the
 * dialog can show a ⚠ warning on rows that already carry a different category
 * (the user is about to override an existing classification).
 *
 * Results are ordered newest-first.  Capped at 200 rows for UI performance.
 */
async function findUnclassifiedMatching(
  companyId: string,
  ruleText: string,
  categoryId: string,
): Promise<ApiResponse<BankTransaction[]>> {
  try {
    const trimmed      = ruleText.trim();
    const ilikePattern = `%${trimmed}%`;

    // Two cases we want to match:
    //   a) accounting_category_id IS NULL           — truly unclassified
    //   b) accounting_category_id != categoryId     — classified differently
    // Both are valid targets for back-fill; (b) needs an override warning in UI.
    const orFilter = `accounting_category_id.is.null,accounting_category_id.neq.${categoryId}`;

    const { data, error } = await supabase
      .from('bank_transactions')
      .select('*, bank_accounts(nickname), accounting_categories(name, cashflow_mapping)')
      .eq('company_id', companyId)
      .ilike('description', ilikePattern)
      .or(orFilter)
      .order('transaction_date', { ascending: false })
      .limit(200);

    console.log(
      '[findUnclassifiedMatching]',
      `search="${trimmed}"`,
      `excludingCategoryId=${categoryId}`,
      `results=${data?.length ?? 0}`,
      error ? `error=${error.message}` : 'ok',
    );

    if (error) {
      console.error('bankCategoryRulesService.findUnclassifiedMatching error:', error);
      return { data: null, error: { message: error.message, code: error.code, details: error }, status: 400 };
    }

    return { data: (data ?? []) as BankTransaction[], error: null, status: 200 };
  } catch (err) {
    console.error('Unexpected error in findUnclassifiedMatching:', err);
    return { data: null, error: { message: 'An unexpected error occurred', details: err }, status: 500 };
  }
}

/**
 * Bulk-update a specific list of bank-transaction IDs to assign `categoryId`.
 *
 * We update in one Supabase `.in()` call.  Any row that was concurrently
 * classified by another session will simply get overwritten — acceptable
 * since the user explicitly confirmed the selection.
 *
 * Returns the number of rows actually updated.
 */
async function bulkApplyCategoryRule(
  transactionIds: string[],
  categoryId: string,
): Promise<ApiResponse<{ updatedCount: number }>> {
  if (transactionIds.length === 0) {
    return { data: { updatedCount: 0 }, error: null, status: 200 };
  }

  try {
    const { data: updatedRows, error } = await supabase
      .from('bank_transactions')
      .update({ accounting_category_id: categoryId })
      .in('id', transactionIds)
      .select('id');

    if (error) {
      console.error('bankCategoryRulesService.bulkApplyCategoryRule error:', error);
      return { data: null, error: { message: error.message, code: error.code, details: error }, status: 400 };
    }

    return { data: { updatedCount: (updatedRows ?? []).length }, error: null, status: 200 };
  } catch (err) {
    console.error('Unexpected error in bulkApplyCategoryRule:', err);
    return { data: null, error: { message: 'An unexpected error occurred', details: err }, status: 500 };
  }
}

// ─── Export ───────────────────────────────────────────────────────────────────

export const bankCategoryRulesService = {
  getRulesForCompany,
  getRulesForCategory,
  createRule,
  deleteRule,
  buildRulesList,
  matchCategory,
  findUnclassifiedMatching,
  bulkApplyCategoryRule,
};
